import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { Doc } from 'src/@types/docs';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { UserRecentFeature } from 'src/generated/graphql';
import { DocsRequests } from 'src/requests/docs';
import { useUserRecentsPersistance } from 'src/sections/@dashboard/recents/shared/useUserRecentsPersistance';
import { QueryKey } from 'src/utils/react-query';
import { useUpdateDocs } from './useUpdateDocs';

export const useUpdateDocMutation = () => {
  const { updateDocs } = useUpdateDocs();
  const queryClient = useQueryClient();
  const { addUserRecent } = useUserRecentsPersistance();

  const addDocUserRecent = useCallback(
    (doc: Partial<Omit<Doc, 'parent'> & { parent: string }>) =>
      addUserRecent({
        title: doc?.title,
        objectRef: doc?._id,
        icon: doc?.icon?.value,
        feature: UserRecentFeature.Docs,
        url: PATH_DASHBOARD.docs.view(doc?._id || ''),
      }),
    [addUserRecent],
  );

  const { mutateAsync: updateDoc, isLoading: isUpdateDocLoading } = useMutation(
    DocsRequests.updateDoc,
    {
      onMutate(doc) {
        const prevDoc = queryClient.getQueryData<Doc>([QueryKey.DOC, doc._id]);

        queryClient.setQueryData<Doc>([QueryKey.DOC, doc._id], () => {
          return {
            ...prevDoc,
            ...doc,
          } as Doc;
        });

        addDocUserRecent(doc);
        updateDocs(doc);
        return { prevDoc };
      },
    },
  );

  return {
    isUpdateDocLoading,
    updateDoc,
  };
};
